
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import PostCard from './PostCard';
import type { User, Band } from '../types';
import { USER_ROLES, OPPORTUNITY_TYPES } from '../types';
import SearchIcon from './icons/SearchIcon';
import CloseIcon from './icons/CloseIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import CheckIcon from './icons/CheckIcon';
import MapPinIcon from './icons/MapPinIcon';

const COMMON_INSTRUMENTS = ['Guitar', 'Bass', 'Drums', 'Vocals', 'Keyboard', 'Piano', 'Saxophone', 'Trumpet', 'Violin', 'DJ', 'Percussion'];
const COMMON_GENRES = ['Rock', 'Pop', 'Jazz', 'Blues', 'Electronic', 'Hip-Hop', 'Classical', 'Folk', 'Metal', 'R&B', 'Soul', 'Funk'];

// Legacy support: mapping localized names to global English naming
const normalizeGeo = (val?: string) => {
    if (!val) return '';
    const map: Record<string, string> = { 'Roma': 'Rome', 'Milano': 'Milan', 'Napoli': 'Naples', 'Firenze': 'Florence' };
    return map[val] || val;
};

const SearchResultsView: React.FC = () => {
  const { displayItems, opportunities } = useOpportunities();
  const { users } = useAuth();
  const { searchTerm, setSearchTerm } = useNavigation();

  // Filter States
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  // Computed Geo Options from ALL data for better coverage
  const geoOptions = useMemo(() => {
    const cities = new Set<string>();
    const regions = new Set<string>();
    const countries = new Set<string>();

    const processItem = (loc?: string, reg?: string, cntry?: string) => {
      if (loc) cities.add(normalizeGeo(loc.split(',')[0].trim()));
      if (reg) regions.add(reg.trim());
      if (cntry) countries.add(cntry.trim());
    };

    opportunities.forEach(o => processItem(o.location, o.region, o.country));
    users.forEach(u => processItem(u.city, u.region, u.country));

    return {
      cities: Array.from(cities).filter(Boolean).sort(),
      regions: Array.from(regions).filter(Boolean).sort(),
      countries: Array.from(countries).filter(Boolean).sort(),
    };
  }, [opportunities, users]);

  const filteredItems = useMemo(() => {
    return displayItems.filter(item => {
      // Role Filter
      if (selectedRoles.length > 0) {
        if (item.resultType === 'user') {
            const userRoles = item.roles || (item.role ? [item.role] : []);
            if (!selectedRoles.some(r => userRoles.includes(r as any))) return false;
        } else if (item.resultType === 'opportunity') {
            if (item.roleNeeded && !selectedRoles.includes(item.roleNeeded)) return false;
        }
      }

      // Type Filter
      if (selectedTypes.length > 0) {
        if (item.resultType === 'opportunity') {
            if (!selectedTypes.includes(item.type)) return false;
        } else return false;
      }

      // Geography Logic with legacy normalization
      const itemCity = normalizeGeo((item.resultType === 'opportunity' ? item.location : item.city)?.split(',')[0].trim());
      const itemRegion = (item.resultType === 'opportunity' ? item.region : (item as any).region);
      const itemCountry = (item.resultType === 'opportunity' ? item.country : (item as any).country);

      if (selectedCities.length > 0 && (!itemCity || !selectedCities.includes(itemCity))) return false;
      if (selectedRegions.length > 0 && (!itemRegion || !selectedRegions.includes(itemRegion))) return false;
      if (selectedCountries.length > 0 && (!itemCountry || !selectedCountries.includes(itemCountry))) return false;

      // Instrument/Genre
      if (selectedInstruments.length > 0) {
        const itemInstr = item.resultType === 'opportunity' ? (item.instrument?.split(',').map(s => s.trim()) || []) : (item.instruments || []);
        if (!selectedInstruments.some(i => itemInstr.includes(i))) return false;
      }
      if (selectedGenres.length > 0) {
        const itemG = item.resultType === 'opportunity' ? (item.genre?.split(',').map(s => s.trim()) || []) : (item.genres || []);
        if (!selectedGenres.some(g => itemG.includes(g))) return false;
      }

      return true;
    });
  }, [displayItems, selectedRoles, selectedTypes, selectedInstruments, selectedGenres, selectedCities, selectedRegions, selectedCountries]);

  const clearFilters = () => {
    setSelectedRoles([]); setSelectedTypes([]); setSelectedInstruments([]); setSelectedGenres([]);
    setSelectedCities([]); setSelectedRegions([]); setSelectedCountries([]);
  };

  const hasActiveFilters = selectedRoles.length > 0 || selectedTypes.length > 0 || selectedInstruments.length > 0 || 
                           selectedGenres.length > 0 || selectedCities.length > 0 || selectedRegions.length > 0 || selectedCountries.length > 0;

  return (
    <div className="space-y-6 animate-in fade-in duration-300 font-urbanist">
      <div className="bg-brand-surface rounded-xl p-6 border border-gray-800 shadow-xl">
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="bg-brand-orange/10 p-2 rounded-lg">
                        <SearchIcon className="h-6 w-6 text-brand-orange" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight uppercase">Instant Explorer</h2>
                        <p className="text-sm text-brand-muted">Matches for "<span className="text-white font-bold">{searchTerm}</span>"</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {hasActiveFilters && (
                        <button onClick={clearFilters} className="text-[10px] font-black text-brand-orange hover:underline uppercase tracking-widest">Clear All</button>
                    )}
                    <button onClick={() => setSearchTerm('')} className="p-2 hover:bg-gray-800 rounded-full text-brand-muted hover:text-white transition-colors flex items-center gap-2 text-xs font-bold tracking-widest uppercase">
                        <CloseIcon className="h-5 w-5" />
                        <span className="hidden sm:inline">Close</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-3">
                <FilterDropdown label="Role" options={USER_ROLES} selected={selectedRoles} onToggle={(v) => setSelectedRoles(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                <FilterDropdown label="Type" options={OPPORTUNITY_TYPES} selected={selectedTypes} onToggle={(v) => setSelectedTypes(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                <FilterDropdown label="Instrument" options={COMMON_INSTRUMENTS} selected={selectedInstruments} onToggle={(v) => setSelectedInstruments(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                <FilterDropdown label="Genre" options={COMMON_GENRES} selected={selectedGenres} onToggle={(v) => setSelectedGenres(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                
                <div className="w-full h-px bg-gray-800 my-1" />
                
                <FilterDropdown label="City" options={geoOptions.cities} selected={selectedCities} onToggle={(v) => setSelectedCities(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} icon={<MapPinIcon className="h-3 w-3" />} />
                <FilterDropdown label="Region" options={geoOptions.regions} selected={selectedRegions} onToggle={(v) => setSelectedRegions(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} icon={<MapPinIcon className="h-3 w-3" />} />
                <FilterDropdown label="Country" options={geoOptions.countries} selected={selectedCountries} onToggle={(v) => setSelectedCountries(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} icon={<MapPinIcon className="h-3 w-3" />} />
            </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-2">
          <p className="text-xs font-bold text-brand-muted uppercase tracking-[0.2em]">Displaying {filteredItems.length} Match{filteredItems.length !== 1 ? 'es' : ''}</p>
      </div>

      <div className="space-y-4">
          {filteredItems.length > 0 ? filteredItems.map(item => {
              if (item.resultType === 'opportunity') return <PostCard key={`opp-${item.id}`} opportunity={item} />;
              if (item.resultType === 'user') return <UserResultCard key={`user-${item.handle}`} user={item} />;
              if (item.resultType === 'band') return <BandResultCard key={`band-${item.id}`} band={item} />;
              return null;
          }) : (
            <div className="bg-brand-surface rounded-2xl p-20 text-center border-2 border-dashed border-gray-800">
              <SearchIcon className="h-10 w-10 text-gray-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-tight">No results</h3>
              <p className="text-brand-muted max-w-sm mx-auto">Try broadening your search or clearing filters.</p>
            </div>
          )}
      </div>
    </div>
  );
};

export const FilterDropdown: React.FC<{ label: string, options: string[], selected: string[], onToggle: (val: string) => void, icon?: React.ReactNode }> = ({ label, options, selected, onToggle, icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    return (
        <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 text-[10px] font-black uppercase tracking-widest transition-all ${selected.length > 0 ? 'border-brand-orange bg-brand-orange/10 text-white' : 'border-gray-700 bg-gray-800/50 text-brand-muted hover:border-gray-600 hover:text-white'}`}>
                {icon}{label} {selected.length > 0 && <span className="bg-brand-orange text-white rounded-full px-1.5 py-0.5 ml-1">{selected.length}</span>}
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-brand-surface border border-gray-700 rounded-xl shadow-2xl z-[60] animate-in slide-in-from-top-2 duration-200 overflow-hidden">
                    <div className="max-h-60 overflow-y-auto custom-scrollbar p-2">
                        {options.length > 0 ? options.map(opt => (
                            <button key={opt} onClick={() => onToggle(opt)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-xs font-bold transition-colors mb-1 last:mb-0 ${selected.includes(opt) ? 'bg-brand-orange/20 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>
                                <span className="truncate">{opt}</span>
                                {selected.includes(opt) && <CheckIcon className="h-4 w-4 text-brand-orange" />}
                            </button>
                        )) : <p className="p-4 text-[10px] text-center text-brand-muted uppercase font-bold tracking-widest">No options</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

const BandResultCard: React.FC<{ band: Band }> = ({ band }) => {
    const { handleViewBand, setSearchTerm } = useNavigation();
    const geo = [band.city, band.region, band.country].filter(Boolean).join(', ');
    return (
        <div onClick={() => { handleViewBand(band); setSearchTerm(''); }} className="flex items-center space-x-4 bg-brand-surface p-4 rounded-xl border border-gray-800 hover:border-brand-orange/50 cursor-pointer transition-all group">
            <img src={band.avatarUrl} alt={band.name} className="h-16 w-16 rounded-xl object-cover border border-gray-700" />
            <div className="flex-1 min-w-0">
                <p className="font-bold text-white text-lg group-hover:text-brand-orange transition-colors truncate tracking-tight">{band.name}</p>
                <p className="text-xs text-brand-muted truncate uppercase font-bold tracking-widest">{geo}</p>
            </div>
        </div>
    );
};

const UserResultCard: React.FC<{ user: User }> = ({ user }) => {
    const { handleViewProfile, setSearchTerm } = useNavigation();
    const geo = [user.city, user.region, user.country].filter(Boolean).join(', ');
    return (
        <div onClick={() => { handleViewProfile(user); setSearchTerm(''); }} className="flex items-center space-x-4 bg-brand-surface p-4 rounded-xl border border-gray-800 hover:border-brand-orange/50 cursor-pointer transition-all group">
            <img src={user.avatarUrl} alt={user.name} className="h-16 w-16 rounded-full object-cover border border-gray-700" />
            <div className="flex-1 min-w-0">
                <p className="font-bold text-white text-lg group-hover:text-brand-orange transition-colors truncate tracking-tight">{user.name}</p>
                <p className="text-sm text-brand-orange font-bold truncate tracking-tighter">{user.title}</p>
                <p className="text-[10px] text-gray-500 truncate mt-1 uppercase font-bold tracking-widest">{geo}</p>
            </div>
        </div>
    );
};

export default SearchResultsView;
